import React from 'react';

import {ClassesDropdown} from 'client/TeacherDashboard/components/ClassesDropdown';
import {ClassworkFilterBar} from 'client/TeacherDashboard/components/ClassworkFilterBar';
import {FilterSelectDropdown} from 'client/TeacherDashboard/components/FilterSelectDropdown';
import {
  CREATED_BY_FILTER_OPTIONS,
  STATUS_FILTER_OPTIONS
} from 'client/TeacherDashboard/config/filterOptions';
import {SortDropdown} from 'client/TeacherDashboard/components/SortDropdown';
import type {
  ClassworkCreatedByFilter,
  ClassworkStatusFilter,
  SelectedClassrooms,
  SelectOption,
  SortValue
} from 'client/TeacherDashboard/types';

import type {GlobalAssignmentFilterObj, GlobalAssignmentSharedOption} from '../constants';
import {GlobalAssignmentsSharedOptions} from '../constants';

export type AssignmentSortValue = 'updated_at' | 'name';
export const DEFAULT_ASSIGNMENT_SORT: SortValue<AssignmentSortValue> = {
  field: 'updated_at',
  order: 'desc'
};

const SORT_OPTIONS: ReadonlyArray<SelectOption<AssignmentSortValue>> = [
  {value: 'updated_at', label: 'Recently Edited'},
  {value: 'name', label: 'Name'}
];

const [SHARED_YOU, SHARED_SOMEONE_ELSE] = GlobalAssignmentsSharedOptions;

const SHARED_BY_FILTER: Record<ClassworkCreatedByFilter, GlobalAssignmentSharedOption | ''> = {
  all: '',
  you: SHARED_YOU,
  someoneElse: SHARED_SOMEONE_ELSE
};

const CREATED_BY_BY_SHARED: Record<GlobalAssignmentFilterObj['shared'], ClassworkCreatedByFilter> =
  {
    '': 'all',
    [SHARED_YOU]: 'you',
    [SHARED_SOMEONE_ELSE]: 'someoneElse'
  };

interface AssignmentClassworkFilterBarProps {
  isDraftsTab: boolean;
  filters: GlobalAssignmentFilterObj;
  searchTerm: string;
  sort: SortValue<AssignmentSortValue>;
  setSearchTerm: (value: string) => void;
  setDebouncedSearchTerm: (value: string) => void;
  setSort: (value: SortValue<AssignmentSortValue>) => void;
  /** Applies the filter immediately — sets both the staged and applied filter state. */
  applyFilters: (filters: GlobalAssignmentFilterObj) => void;
}

export const AssignmentClassworkFilterBar = ({
  isDraftsTab,
  filters,
  searchTerm,
  sort,
  setSearchTerm,
  setDebouncedSearchTerm,
  setSort,
  applyFilters
}: AssignmentClassworkFilterBarProps) => {
  const handleStatusChange = (status: ClassworkStatusFilter) =>
    applyFilters({...filters, status: status === 'all' ? '' : status});

  const handleCreatedByChange = (createdBy: ClassworkCreatedByFilter) =>
    applyFilters({...filters, shared: SHARED_BY_FILTER[createdBy]});

  const handleClassesChange = (classes: SelectedClassrooms) => applyFilters({...filters, classes});

  return (
    <ClassworkFilterBar
      searchPlaceholder={isDraftsTab ? 'Search by draft' : 'Search by assignment'}
      search={searchTerm}
      onSearchChange={setSearchTerm}
      onDebouncedSearchChange={setDebouncedSearchTerm}
    >
      {/* Drafts aren't assigned yet, so status/created-by don't apply — classes + sort only. */}
      {!isDraftsTab && (
        <FilterSelectDropdown
          label='Status'
          value={(filters.status || 'all') as ClassworkStatusFilter}
          options={STATUS_FILTER_OPTIONS}
          onChange={handleStatusChange}
          testId='status-trigger'
        />
      )}
      <ClassesDropdown selected={filters.classes} onChange={handleClassesChange} />
      {!isDraftsTab && (
        <FilterSelectDropdown
          label='Created By'
          value={CREATED_BY_BY_SHARED[filters.shared]}
          options={CREATED_BY_FILTER_OPTIONS}
          onChange={handleCreatedByChange}
          testId='created-by-trigger'
        />
      )}
      <SortDropdown value={sort} options={SORT_OPTIONS} onChange={setSort} />
    </ClassworkFilterBar>
  );
};
